/**
 * The shell.
 *
 * Stage 0's exit criterion is a logged-in user reaching an *empty* shell on the
 * real subdomain. Empty is correct — filling it is Stage A for HQ and Stage B
 * for checkpoint logging. What step 06 adds is only how someone gets here and
 * which of the two empty shells they land on.
 */

import { Link, Route, Routes, useLocation } from 'react-router';

import { signOut } from './data/auth.js';
import { needsCheckpoint, shellFor, type Membership } from './data/members.js';
import { CheckpointEditor } from './CheckpointEditor.js';
import { CheckpointList } from './CheckpointList.js';
import { ChooseLocation } from './ChooseLocation.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { EventSettings } from './EventSettings.js';
import { RuntimeProblemBanners, useRuntimeProblems } from './RuntimeProblems.js';
import { SignIn } from './SignIn.js';
import { useSession } from './useSession.js';

export function App(): React.JSX.Element {
  const { problems, dismiss } = useRuntimeProblems();

  return (
    <>
      {/*
        D57. Above everything, so a background failure is visible whatever
        phase the app is in — including the sign-in screen, where step 06's
        invisible rejection actually happened.
      */}
      <RuntimeProblemBanners problems={problems} dismiss={dismiss} />
      <Shell />
    </>
  );
}

function Shell(): React.JSX.Element {
  const phase = useSession();

  if (phase.kind === 'loading') {
    return <p>Loading…</p>;
  }

  if (phase.kind === 'signedOut') {
    return <SignIn />;
  }

  if (phase.kind === 'noEvent') {
    return (
      <main>
        <h1>Not on an event, yet</h1>
        <p>
          You are signed in as {phase.session.email}, but you have not been added to an event.
          The organiser adds people; ask them and then reload.
        </p>
        <SignOutButton />
      </main>
    );
  }

  const { membership, session } = phase;

  // D43: crew must say where they are before the shell is any use — the
  // security rules bind their log entries to that checkpoint, so without it
  // they could not write anything anyway.
  if (needsCheckpoint(membership.role) && membership.checkpointId === null) {
    return <ChooseLocation membership={membership} uid={session.uid} />;
  }

  const shell = shellFor(membership.role);
  if (shell === 'hq') return <HqShell membership={membership} />;
  if (shell === 'media') return <MediaShell membership={membership} />;
  return <CheckpointShell membership={membership} />;
}

/**
 * D55: the HQ side is routed, because Stage A is several admin screens rather
 * than one. The checkpoint side stays a single view until Stage B gives it
 * somewhere to go.
 */
function HqShell({ membership }: { readonly membership: Membership }): React.JSX.Element {
  const { pathname } = useLocation();

  return (
    <>
      <header>
        <h1>Race HQ</h1>
        <nav>
          <Link to="/">Overview</Link> <Link to="/event">Event settings</Link>{' '}
          <Link to="/checkpoints">Checkpoints</Link>
        </nav>
        <p>
          Signed in as {membership.displayName ?? 'you'} — {membership.role}.
        </p>
        <SignOutButton />
      </header>
      <main>
        {/*
          D57: the boundary is per route, not only at the top, so a crash in an
          admin screen cannot take the shell — header, navigation and sign-out —
          with it. Keying it by pathname means navigating away clears a broken
          screen instead of leaving it stuck until a reload.
        */}
        <ErrorBoundary key={pathname} what="This screen">
          <Routes>
            <Route index element={<HqOverview />} />
            <Route path="/event" element={<EventSettings membership={membership} />} />
            <Route path="/checkpoints" element={<CheckpointList membership={membership} />} />
            {/* Static before dynamic, so /checkpoints/new is never read as an id. */}
            <Route
              path="/checkpoints/new"
              element={<CheckpointEditor membership={membership} mode="create" />}
            />
            <Route
              path="/checkpoints/:checkpointId"
              element={<CheckpointEditor membership={membership} mode="edit" />}
            />
            <Route path="*" element={<NoSuchPage />} />
          </Routes>
        </ErrorBoundary>
      </main>
    </>
  );
}

function HqOverview(): React.JSX.Element {
  return (
    <section>
      <h2>Overview</h2>
      <p>
        The master board is Stage B. Stage A is admin and rota:{' '}
        <Link to="/event">event settings</Link> and <Link to="/checkpoints">checkpoints</Link> so
        far.
      </p>
    </section>
  );
}

function NoSuchPage(): React.JSX.Element {
  return (
    <section>
      <h2>Nothing here</h2>
      <p>
        That address does not exist. <Link to="/">Back to the overview</Link>.
      </p>
    </section>
  );
}

/**
 * D58's third shell. Read-only, phone-first, and deliberately not the HQ board:
 * press get where people are and *that* someone is flagged, never why — the
 * reason is a welfare observation about an identifiable person, and for it they
 * ring Race Control or the checkpoint lead.
 *
 * Empty for now. The board it will read from is Stage B.
 */
function MediaShell({ membership }: { readonly membership: Membership }): React.JSX.Element {
  return (
    <main>
      <h1>Cairn — media</h1>
      <p>Signed in as {membership.displayName ?? 'you'}.</p>
      <p>Empty, which is correct. The participant board is Stage B.</p>
      <SignOutButton />
    </main>
  );
}

function CheckpointShell({ membership }: { readonly membership: Membership }): React.JSX.Element {
  return (
    <main>
      <h1>Checkpoint</h1>
      <p>
        Signed in as {membership.displayName ?? 'you'} — {membership.role}
        {membership.checkpointId === null ? '' : ` at ${membership.checkpointId}`}.
      </p>
      <p>Empty, which is correct. Logging is Stage B.</p>
      <SignOutButton />
    </main>
  );
}

/**
 * D40: relocation is sign-out then sign-in. This is therefore not just a
 * courtesy — it is half of how someone moves between checkpoints.
 */
function SignOutButton(): React.JSX.Element {
  return (
    <button type="button" onClick={() => void signOut()}>
      Sign out
    </button>
  );
}
